import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMenuIcon } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Paperclip, X, ChevronLeft } from 'lucide-react';
import { inquiryApi } from '@/api/inquiryApi';
import { IS_PRODUCTION } from '@/api/apiConfig';
import { Spinner } from '@/components/ui/spinner';

const MAX_FILE_SIZE = IS_PRODUCTION ? 1 * 1024 * 1024 : 10 * 1024 * 1024; // 1MB prod / 10MB dev
const MAX_TITLE_LEN = 200;
const MIN_CONTENT_LEN = 10;
const MAX_CONTENT_LEN = 2000;

export default function InquiryNewPage() {
  const navigate = useNavigate();
  const menuIcon = useMenuIcon();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > MAX_FILE_SIZE) {
      setError(`파일 크기는 ${IS_PRODUCTION ? '1MB' : '10MB'} 이하여야 합니다.`);
      e.target.value = '';
      return;
    }
    setFile(selected);
    setError(null);
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = (): string | null => {
    if (!title.trim()) return '제목을 입력해주세요.';
    if (title.trim().length > MAX_TITLE_LEN) return `제목은 ${MAX_TITLE_LEN}자 이하여야 합니다.`;
    if (!content.trim()) return '내용을 입력해주세요.';
    if (content.trim().length < MIN_CONTENT_LEN) return `내용은 ${MIN_CONTENT_LEN}자 이상 입력해주세요.`;
    if (content.trim().length > MAX_CONTENT_LEN) return `내용은 ${MAX_CONTENT_LEN}자 이하여야 합니다.`;
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await inquiryApi.create({
        title: title.trim(),
        content: content.trim(),
        isSecret,
        file: file ?? undefined,
      });
      navigate('/dashboard/inquiry');
    } catch {
      setError('문의 등록 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title="1:1 문의"
        description="문의 내용을 작성해주세요."
        icon={menuIcon}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">문의 작성</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* 제목 */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="title">제목 <span className="text-red-500">*</span></Label>
                <span className="text-xs text-gray-400">{title.length}/{MAX_TITLE_LEN}</span>
              </div>
              <Input
                id="title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                maxLength={MAX_TITLE_LEN}
                placeholder="문의 제목을 입력해주세요."
                disabled={submitting}
              />
            </div>

            {/* 내용 */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="content">내용 <span className="text-red-500">*</span></Label>
                <span className={`text-xs ${content.length > MAX_CONTENT_LEN ? 'text-red-500' : 'text-gray-400'}`}>
                  {content.length}/{MAX_CONTENT_LEN}
                </span>
              </div>
              <textarea
                id="content"
                value={content}
                onChange={e => setContent(e.target.value)}
                maxLength={MAX_CONTENT_LEN}
                rows={10}
                placeholder={`문의 내용을 ${MIN_CONTENT_LEN}자 이상 상세히 입력해주세요.`}
                disabled={submitting}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>

            {/* 비밀글 토글 */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isSecret"
                checked={isSecret}
                onChange={e => setIsSecret(e.target.checked)}
                disabled={submitting}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <Label htmlFor="isSecret" className="cursor-pointer font-normal text-gray-700">
                🔒 비밀글로 등록
              </Label>
              <span className="text-xs text-gray-400">(관리자와 본인만 열람 가능)</span>
            </div>

            {/* 파일 첨부 */}
            <div className="space-y-1.5">
              <Label>첨부파일</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting || !!file}
                >
                  <Paperclip className="w-4 h-4 mr-2" />
                  파일 선택
                </Button>
                <span className="text-xs text-gray-400">
                  최대 1개 · {IS_PRODUCTION ? '1MB' : '10MB'} 이하
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                disabled={submitting}
              />
              {file && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-50 border text-sm">
                  <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="flex-1 truncate text-gray-700">{file.name}</span>
                  <span className="text-gray-400 text-xs flex-shrink-0">
                    {(file.size / 1024).toFixed(0)}KB
                  </span>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    disabled={submitting}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 버튼 영역 */}
        <div className="flex justify-between mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/inquiry')}
            disabled={submitting}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            취소
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <><Spinner size="sm" className="mr-2" />등록 중...</>
            ) : (
              '문의 등록'
            )}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
